import getRpgLogsApiAccessToken from './getRpgLogsApiAccessToken';
import config, { type GameSlug, getBaseUrl } from '../config';
import { getRequestMetadataHeaders } from './rpgLogsApiRequestMetadata';

type FetchJsonFromRpgLogsApiOptions = {
  query: string;
  variables?: Record<string, unknown>;
  gameSlug?: string | null;
  retryOnFailure?: boolean;
  attemptNumber?: number;
  userAccessToken?: string;
  locale?: string | null;
};

async function fetchJsonFromRpgLogsApi({
  query,
  variables = {},
  gameSlug = undefined,
  retryOnFailure = true,
  attemptNumber = 1,
  userAccessToken = undefined,
  locale = undefined,
}: FetchJsonFromRpgLogsApiOptions) {
  const body = JSON.stringify({ query, variables });
  const requestMetadataHeaders = getRequestMetadataHeaders();

  try {
    const accessToken =
      userAccessToken ?? (await getRpgLogsApiAccessToken(gameSlug, locale));
    const url = `${getBaseUrl(gameSlug as GameSlug, locale)}/api/v2/${
      userAccessToken ? 'archon-user' : 'archon'
    }${config.isDevelopment ? '?XDEBUG_SESSION_START=PHPSTORM' : ''}`;

    if (config.isDevelopment) {
      console.log({
        url,
        body,
      });
    }

    const headers: Record<string, string> = {
      Authorization: 'Bearer ' + accessToken,
      'Content-Type': 'application/json',
    };

    if (process.env.STAGING_ACCESS_SECRET) {
      headers['x-staging-access-secret'] = process.env.STAGING_ACCESS_SECRET;
    }
    for (const [key, value] of Object.entries(requestMetadataHeaders)) {
      if (value !== undefined) {
        headers[key] = value;
      }
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body,
    });

    let json = null;
    const text = await response.text();
    try {
      json = JSON.parse(text);
    } catch (error) {
      if (config.isDevelopment) {
        console.log({
          url,
          body: { query, variables },
          error,
          text,
        });
      }

      throw 'RPGLogs GraphQL response did not contain JSON.';
    }

    if (json.errors) {
      throw {
        message: 'RPGLogs GraphQL response contained errors.',
        errors: JSON.stringify(json.errors, null, 2),
        query,
        variables,
      };
    }

    const data = json?.data;

    if (!data) {
      throw {
        message: 'RPGLogs GraphQL response did not return data or errors.',
        json: JSON.stringify(json, null, 2),
        query,
        variables,
      };
    }

    return data;
  } catch (error) {
    if (config.isCI && retryOnFailure && attemptNumber < 3) {
      const delay = 5000 * attemptNumber;

      console.warn(
        error,
        body,
        requestMetadataHeaders['x-archon-route'],
        requestMetadataHeaders['x-archon-path'],
      );
      console.log(
        `RPGLogs GraphQL request failed, retrying in ${delay}ms (${attemptNumber}/3)...`,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));

      return await fetchJsonFromRpgLogsApi({
        query,
        variables,
        gameSlug,
        retryOnFailure,
        attemptNumber: attemptNumber + 1,
        userAccessToken,
        locale,
      });
    }

    throw error;
  }
}

export default fetchJsonFromRpgLogsApi;
